import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, ChevronRight, Plus, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const DAYS = ["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"];
const MONTHS = ["Januari", "Februari", "Mars", "April", "Maj", "Juni", "Juli", "Augusti", "September", "Oktober", "November", "December"];
const TIME_SLOTS = ["08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00"];
const statusColors = { pending_payment: "bg-yellow-500", pending_approval: "bg-orange-500", approved: "bg-green-600" };

function getDaysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function getFirstDay(y, m) { const d = new Date(y, m, 1).getDay(); return d === 0 ? 6 : d - 1; }

export default function AdminCalendar() {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth());
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [selectedDate, setSelectedDate] = useState("");
    const [form, setForm] = useState({ date: "", time: "10:00", model: "small", name: "", email: "", phone: "", delivery: "pickup", notes: "", status: "approved" });
    const [creating, setCreating] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [sendingPayment, setSendingPayment] = useState(false);

    const fetchBookings = () => {
        api.get(`/calendar/bookings?month=${month + 1}&year=${year}`)
            .then((r) => setBookings(r.data)).catch(console.error).finally(() => setLoading(false));
    };

    useEffect(() => { fetchBookings(); }, [month, year]);

    const prevMonth = () => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); };
    const nextMonth = () => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); };

    const openCreateForDate = (day) => {
        const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        setSelectedDate(dateStr);
        setForm({ ...form, date: dateStr });
        setShowCreate(true);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            await api.post("/admin/bookings", form);
            toast.success("Bokning skapad");
            setShowCreate(false);
            setForm({ date: "", time: "10:00", model: "small", name: "", email: "", phone: "", delivery: "pickup", notes: "", status: "approved" });
            fetchBookings();
        } catch (err) { toast.error(err.response?.data?.detail || "Misslyckades"); }
        finally { setCreating(false); }
    };

    const handleSendPayment = async () => {
        if (!selectedBooking) return;
        setSendingPayment(true);
        try {
            const res = await api.post(`/admin/bookings/${selectedBooking.id}/payment-link`, {
                booking_id: selectedBooking.id,
                origin_url: window.location.origin,
                description: `Birò ${selectedBooking.model === "small" ? "Small" : "Big"} provkörning – ${selectedBooking.date}`,
            });
            toast.success("Betalningslänk skickad till kunden");
            setSelectedBooking(null);
            fetchBookings();
        } catch (err) { toast.error(err.response?.data?.detail || "Misslyckades"); }
        finally { setSendingPayment(false); }
    };

    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDay(year, month);
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const emptyDays = Array.from({ length: firstDay }, (_, i) => i);
    const getBookingsForDay = (day) => {
        const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        return bookings.filter((b) => b.date === dateStr);
    };

    return (
        <div className="p-8" data-testid="admin-calendar">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-medium tracking-tight">Kalender</h1>
                <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={prevMonth} data-testid="cal-prev"><ChevronLeft size={16} /></Button>
                    <span className="text-sm font-medium min-w-[160px] text-center">{MONTHS[month]} {year}</span>
                    <Button variant="outline" size="sm" onClick={nextMonth} data-testid="cal-next"><ChevronRight size={16} /></Button>
                </div>
            </div>

            <Card className="bg-white border-neutral-200 shadow-none rounded-md">
                <CardContent className="p-0">
                    <div className="grid grid-cols-7 border-b border-neutral-200">
                        {DAYS.map((d) => <div key={d} className="p-3 text-center text-xs uppercase tracking-wider text-neutral-500 font-medium">{d}</div>)}
                    </div>
                    <div className="grid grid-cols-7">
                        {emptyDays.map((i) => <div key={`e-${i}`} className="min-h-[100px] border-b border-r border-neutral-100 bg-neutral-50" />)}
                        {days.map((day) => {
                            const dayBookings = getBookingsForDay(day);
                            const isToday = day === now.getDate() && month === now.getMonth() && year === now.getFullYear();
                            return (
                                <div key={day} className={`min-h-[100px] border-b border-r border-neutral-100 p-2 group cursor-pointer hover:bg-neutral-50 ${isToday ? "bg-blue-50/30" : ""}`}
                                     onClick={() => openCreateForDate(day)} data-testid={`cal-day-${day}`}>
                                    <div className="flex items-center justify-between">
                                        <span className={`text-xs font-medium ${isToday ? "bg-black text-white w-6 h-6 rounded-full flex items-center justify-center" : "text-neutral-600"}`}>{day}</span>
                                        <Plus size={12} className="text-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                    <div className="mt-1 space-y-1">
                                        {dayBookings.map((b) => (
                                            <div key={b.id} className={`text-[10px] p-1 text-white rounded truncate cursor-pointer ${statusColors[b.status] || "bg-black"}`}
                                                 onClick={(e) => { e.stopPropagation(); setSelectedBooking(b); }} data-testid={`cal-booking-${b.id}`}>
                                                {b.time || ""} {b.name} · {b.model}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Create Manual Booking */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-lg" data-testid="create-manual-booking-dialog">
                    <DialogHeader><DialogTitle>Ny manuell bokning – {selectedDate}</DialogTitle></DialogHeader>
                    <form onSubmit={handleCreate} className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs uppercase tracking-wider">Modell</Label>
                                <Select value={form.model} onValueChange={(v) => setForm({ ...form, model: v })}>
                                    <SelectTrigger className="mt-1" data-testid="manual-model"><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="small">Birò Small</SelectItem><SelectItem value="big">Birò Big</SelectItem></SelectContent>
                                </Select></div>
                            <div><Label className="text-xs uppercase tracking-wider">Tid</Label>
                                <Select value={form.time} onValueChange={(v) => setForm({ ...form, time: v })}>
                                    <SelectTrigger className="mt-1" data-testid="manual-time"><SelectValue /></SelectTrigger>
                                    <SelectContent>{TIME_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                                </Select></div>
                        </div>
                        <div><Label className="text-xs uppercase tracking-wider">Namn</Label><Input data-testid="manual-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs uppercase tracking-wider">E-post</Label><Input data-testid="manual-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1" /></div>
                            <div><Label className="text-xs uppercase tracking-wider">Telefon</Label><Input data-testid="manual-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs uppercase tracking-wider">Leverans</Label>
                                <Select value={form.delivery} onValueChange={(v) => setForm({ ...form, delivery: v })}>
                                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="pickup">Nacka</SelectItem><SelectItem value="delivery">Stockholm (+300kr)</SelectItem></SelectContent>
                                </Select></div>
                            <div><Label className="text-xs uppercase tracking-wider">Status</Label>
                                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="approved">Godkänd</SelectItem><SelectItem value="pending_payment">Väntar betalning</SelectItem><SelectItem value="pending_approval">Väntar godkännande</SelectItem></SelectContent>
                                </Select></div>
                        </div>
                        <div><Label className="text-xs uppercase tracking-wider">Anteckningar</Label><Textarea data-testid="manual-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
                        <Button data-testid="submit-manual-booking" type="submit" disabled={creating} className="w-full bg-black text-white">
                            {creating ? <Loader2 size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />}
                            {creating ? "Skapar..." : "Skapa bokning"}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Booking Detail + Payment Link */}
            <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
                <DialogContent className="sm:max-w-md" data-testid="booking-payment-dialog">
                    <DialogHeader><DialogTitle>Bokning</DialogTitle></DialogHeader>
                    {selectedBooking && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div><span className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">Namn</span>{selectedBooking.name}</div>
                                <div><span className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">E-post</span>{selectedBooking.email}</div>
                                <div><span className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">Datum</span>{selectedBooking.date} {selectedBooking.time}</div>
                                <div><span className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">Modell</span>Birò {selectedBooking.model === "small" ? "Small" : "Big"}</div>
                                <div><span className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">Status</span><Badge className="text-[10px]">{selectedBooking.status}</Badge></div>
                            </div>
                            {(selectedBooking.status === "approved" || selectedBooking.status === "pending_payment") && !selectedBooking.payment_session_id && (
                                <Button data-testid="send-payment-link-btn" onClick={handleSendPayment} disabled={sendingPayment} className="w-full bg-black text-white mt-4">
                                    {sendingPayment ? <Loader2 size={14} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
                                    {sendingPayment ? "Skickar..." : "Skicka betalningslänk (500 kr)"}
                                </Button>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
